import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * WorkspaceTabs — a Bloomberg/Fincept-style tabbed workspace shell.
 *
 * Each tab carries its own context (e.g. a different ticker, a different
 * pitch report). Tabs are persisted in localStorage under `storageKey` so a
 * refresh restores the workspace.
 *
 * Tabs are closable, reorderable via drag, and addable via a "+" button at the
 * far right. Selecting a tab swaps the `renderContent(tab)` body underneath.
 *
 * Props:
 *   storageKey      — string. Where to persist {tabs, activeId}.
 *   defaultTab      — Tab. Used when storage is empty.
 *   renderContent   — (tab) => ReactNode. Body for the active tab.
 *   onNewTab        — () => Tab | null | Promise<Tab | null>. Called when the
 *                     "+" button is clicked. Return null (or resolve to null)
 *                     to suppress. Async is supported so callers can prompt via
 *                     a dialog instead of window.prompt.
 *   maxTabs         — number. Default 8.
 *
 * Tab shape:
 *   { id: string, label: string, subtitle?: string, payload?: object, sticky?: bool }
 */
export default function WorkspaceTabs({
  storageKey,
  defaultTab,
  renderContent,
  onNewTab,
  maxTabs = 8,
}) {
  const [{ tabs, activeId }, setState] = useState(() => loadFromStorage(storageKey, defaultTab));
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ tabs, activeId }));
    } catch (e) { /* swallow quota errors */ }
  }, [storageKey, tabs, activeId]);

  const activate = useCallback((id) => {
    setState((s) => ({ ...s, activeId: id }));
  }, []);

  const closeTab = useCallback((id, ev) => {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    setState((s) => {
      const tab = s.tabs.find((t) => t.id === id);
      if (tab && tab.sticky) return s;
      const next = s.tabs.filter((t) => t.id !== id);
      if (next.length === 0 && defaultTab) return { tabs: [defaultTab], activeId: defaultTab.id };
      const activeId = s.activeId === id ? (next[0]?.id || null) : s.activeId;
      return { tabs: next, activeId };
    });
  }, [defaultTab]);

  const addTab = useCallback(async () => {
    if (typeof onNewTab !== 'function') return;
    // onNewTab may return a Tab synchronously or a Promise<Tab|null> (e.g. when
    // it opens a dialog). await handles both.
    const tab = await onNewTab();
    if (!tab) return;
    setState((s) => {
      if (s.tabs.length >= maxTabs) return s;
      if (s.tabs.find((t) => t.id === tab.id)) {
        return { ...s, activeId: tab.id };
      }
      return { tabs: [...s.tabs, tab], activeId: tab.id };
    });
  }, [onNewTab, maxTabs]);

  const onDragStart = (id) => setDragId(id);
  const onDragOver  = (e) => e.preventDefault();
  const onDrop      = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setState((s) => {
      const next = [...s.tabs];
      const from = next.findIndex((t) => t.id === dragId);
      const to   = next.findIndex((t) => t.id === targetId);
      if (from < 0 || to < 0) return s;
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return { ...s, tabs: next };
    });
    setDragId(null);
  };

  const active = useMemo(
    () => tabs.find((t) => t.id === activeId) || tabs[0] || null,
    [tabs, activeId],
  );

  return (
    <div className="flex h-full flex-col">
      <div
        className="flex items-stretch border-b border-border bg-muted/30 overflow-x-auto"
        role="tablist"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={t.id === activeId}
            draggable
            onDragStart={() => onDragStart(t.id)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(t.id)}
            onClick={() => activate(t.id)}
            className={cn(
              'group flex items-center gap-2 border-r border-border px-3 py-2 text-sm select-none',
              t.id === activeId
                ? 'bg-background text-foreground border-b-2 border-b-primary -mb-px'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            )}
            title={t.subtitle ? `${t.label} — ${t.subtitle}` : t.label}
          >
            <span className="truncate max-w-[12rem]">{t.label}</span>
            {t.subtitle ? (
              <span className="text-[10px] text-muted-foreground hidden md:inline">
                {t.subtitle}
              </span>
            ) : null}
            {!t.sticky && (
              <span
                role="button"
                tabIndex={0}
                onClick={(ev) => closeTab(t.id, ev)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') closeTab(t.id, ev); }}
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground/70 opacity-0 group-hover:opacity-100 hover:bg-muted hover:text-foreground"
                aria-label={`Close ${t.label}`}
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </button>
        ))}
        {typeof onNewTab === 'function' && tabs.length < maxTabs && (
          <button
            type="button"
            onClick={addTab}
            className="flex items-center px-2 text-muted-foreground hover:text-foreground hover:bg-muted/50"
            title="Open new workspace tab"
            aria-label="New tab"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0">
        {active ? renderContent(active) : null}
      </div>
    </div>
  );
}

function loadFromStorage(storageKey, defaultTab) {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.tabs) && parsed.tabs.length > 0) {
        return { tabs: parsed.tabs, activeId: parsed.activeId || parsed.tabs[0].id };
      }
    }
  } catch (e) { /* fallthrough */ }
  if (defaultTab) return { tabs: [defaultTab], activeId: defaultTab.id };
  return { tabs: [], activeId: null };
}
